import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import CookieConsent from "react-cookie-consent";
import service1 from "../assets/images/service1.jpg";
import service2 from "../assets/images/service2.jpg";
import service3 from "../assets/images/service3.jpg";

const Home = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const featuredServices = [
    {
      title: t("home.services.profiles"),
      description: t("home.services.profiles_desc"),
      image: service1,
    },
    {
      title: t("home.services.studio"),
      description: t("home.services.studio_desc"),
      image: service2,
    },
    {
      title: t("home.services.cloud"),
      description: t("home.services.cloud_desc"),
      image: service3,
    },
  ];

  const images = featuredServices.map((service) => service.image);

  return (
    <div className="container mx-auto p-4 sm:p-6 md:p-8">
      {/* Hero Section (No Border) */}
      <motion.section
        className="text-center py-12 sm:py-16 rounded-lg mb-8 sm:mb-12"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        role="banner"
        aria-labelledby="hero-title"
      >
        <motion.h1
          id="hero-title"
          className="text-3xl sm:text-4xl md:text-5xl font-bold accent-text mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {t("home.hero.title")}
        </motion.h1>
        <motion.p
          className="text-lg sm:text-xl mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t("home.hero.subtitle")}
        </motion.p>
        <motion.a
          href={`/${lang || "en"}/services`}
          className="inline-block px-6 py-3 bg-transparent border border-gray-500 accent-text hover:bg-gray-500 hover:text-white rounded-lg transition-all text-sm sm:text-base"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          role="button"
          aria-label={t("home.hero.cta")}
        >
          {t("home.hero.cta")}
        </motion.a>
      </motion.section>

      {/* Featured Services */}
      <motion.section
        className="py-8 sm:py-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        role="region"
        aria-labelledby="services-title"
      >
        <h2 id="services-title" className="text-2xl sm:text-3xl font-bold text-center accent-text mb-6 sm:mb-8">
          {t("home.services.title")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {featuredServices.map((service, index) => (
            <motion.div
              key={index}
              className="glass p-4 sm:p-6 rounded-lg shadow-lg text-center card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <LazyLoadImage
                src={service.image}
                alt={service.title}
                effect="blur"
                className="service-image mb-4 cursor-pointer"
                onClick={() => {
                  setPhotoIndex(index);
                  setIsOpen(true);
                }}
                onError={(e) => {
                  console.error("Service image failed to load:", service.image);
                  e.target.src = service1; // Fallback
                }}
              />
              <h3 className="text-lg sm:text-xl font-semibold accent-text mb-2">{service.title}</h3>
              <p className="text-sm sm:text-base">{service.description}</p>
            </motion.div>
          ))}
        </div>
        {isOpen && (
          <Lightbox
            mainSrc={images[photoIndex]}
            nextSrc={images[(photoIndex + 1) % images.length]}
            prevSrc={images[(photoIndex + images.length - 1) % images.length]}
            onCloseRequest={() => setIsOpen(false)}
            onMovePrevRequest={() =>
              setPhotoIndex((photoIndex + images.length - 1) % images.length)
            }
            onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % images.length)}
          />
        )}
      </motion.section>

      {/* Testimonials */}
      <motion.section
        className="py-8 sm:py-12 bg-gray-100"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        role="region"
        aria-labelledby="testimonials-title"
      >
        <h2 id="testimonials-title" className="text-2xl sm:text-3xl font-bold text-center accent-text mb-6 sm:mb-8">
          {t("home.testimonials.title")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {Array(3)
            .fill()
            .map((_, index) => (
              <motion.div
                key={index}
                className="glass p-4 sm:p-6 rounded-lg shadow-lg text-center card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <p className="mb-4 text-sm sm:text-base">{t(`home.testimonials.testimonial${index + 1}`)}</p>
                <p className="font-semibold accent-text text-sm sm:text-base">{t(`home.testimonials.author${index + 1}`)}</p>
              </motion.div>
            ))}
        </div>
      </motion.section>

      {/* Why Choose BRT */}
      <motion.section
        className="py-8 sm:py-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        role="region"
        aria-labelledby="why-choose-title"
      >
        <h2 id="why-choose-title" className="text-2xl sm:text-3xl font-bold text-center accent-text mb-6 sm:mb-8">
          {t("home.why_choose.title")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-center">
          {["users", "projects", "uptime"].map((stat, index) => (
            <motion.div
              key={stat}
              className="glass p-4 sm:p-6 rounded-lg card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold accent-text mb-2">{t(`home.why_choose.${stat}_value`)}</h3>
              <p className="text-sm sm:text-base">{t(`home.why_choose.${stat}`)}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Cookie Consent */}
      <CookieConsent
        location="bottom"
        buttonText={t("home.cookie_accept")}
        cookieName="brtCookieConsent"
        style={{ background: "#f2f3f7", color: "#111827", fontSize: "0.875rem" }}
        buttonStyle={{ background: "#4b5563", color: "white", borderRadius: "0.5rem", padding: "0.5rem 1rem" }}
        expires={150}
      >
        {t("home.cookie_message")}
      </CookieConsent>
    </div>
  );
};

export default Home;